import type { PayrollReviewRun } from './types';
import { TAX_NOT_READY_APPROVE_REASON } from './reviewCopy';

export type RegisterLoadKind = 'loading' | 'ready' | 'notFound' | 'forbidden' | 'error';

/**
 * Why Approve is unavailable to the viewer, or null when it is available. One rule shared by Payroll Review's own run page
 * and Custom Pay Run's Pending Payroll step, so the two can never disagree.
 *
 * This is a courtesy pre-check only — the backend is the authority and re-checks every rule (including the third
 * segregation rule, "ran a calculation in the run", which the client cannot see). Precedence: the viewer's own role in the
 * run first, then the payroll register, which must have loaded with at least one line so a run is never approved without its
 * figures in front of the reviewer.
 */
export function approveBlockedReason(args: {
  viewerUserId: string | null | undefined;
  run: PayrollReviewRun;
  registerKind: RegisterLoadKind;
  registerLines: number;
}): string | null {
  const { viewerUserId, run, registerKind, registerLines } = args;
  const viewerCreated = !!viewerUserId && viewerUserId === run.createdByUserId;
  const viewerSubmitted = !!viewerUserId && !!run.submittedByUserId && viewerUserId === run.submittedByUserId;
  if (viewerCreated || viewerSubmitted) {
    return `You ${viewerCreated ? 'created' : 'submitted'} this payroll run, so a different authorized user must approve it.`;
  }
  if (run.taxReadiness?.status === 'NotReady') return TAX_NOT_READY_APPROVE_REASON;
  if (registerKind === 'loading') return 'Approve becomes available once the payroll register has loaded, so you can review the figures first.';
  if (registerKind === 'error' || registerKind === 'notFound') {
    return 'Approve is unavailable because the payroll register could not be loaded. Retry loading it to review the figures before approving.';
  }
  if (registerKind === 'forbidden') return "Approve is unavailable because you don't have permission to view the payroll register for this run.";
  if (registerLines === 0) return 'Approve is unavailable: there are no payroll figures available to review for this run.';
  return null;
}

/**
 * True when the viewer prepared or submitted the run — the people who may never approve it. A courtesy view-model rule (what
 * to show), never authorization: the server enforces the segregation of duties. `submittedByUserId` is null until the run
 * has been sent for approval.
 */
export function viewerProcessedRun(args: {
  viewerUserId: string | null | undefined;
  createdByUserId: string | null | undefined;
  submittedByUserId?: string | null;
}): boolean {
  const { viewerUserId, createdByUserId, submittedByUserId } = args;
  if (!viewerUserId) return true; // an unidentified viewer is never offered Approve
  return viewerUserId === createdByUserId || (!!submittedByUserId && viewerUserId === submittedByUserId);
}
